import { useEffect, useState } from "react";
import { X, Loader2, Search, Users, Check } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

const ROLE_LABEL = {
  owner: "Owner", manager: "Mgr", admin: "Admin", rep: "Rep",
  technician: "Tech", "support-officer": "Support",
};

export default function ShareSessionModal({ session, onClose, onSaved }) {
  const [staff, setStaff] = useState([]);
  const [picked, setPicked] = useState(new Set(session.shared_with || []));
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/engineering/staff");
        setStaff(data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load staff.");
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/engineering/sessions/${session.id}/share`, {
        user_ids: Array.from(picked),
      });
      toast.success(picked.size === 0 ? "Sharing removed." : `Shared with ${picked.size}.`);
      onSaved?.(Array.from(picked));
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const filtered = staff.filter((u) => {
    if (!q) return true;
    const s = (u.name || u.email || "").toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-start sm:items-center justify-center p-4 pt-16 sm:pt-4" onClick={onClose} data-testid="eng-share-modal">
      <div className="hv-card w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#1FB6C1]" />
            <div className="font-display font-bold text-sm">Share session (read-only)</div>
          </div>
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-3 pt-2 text-[11px] text-[#a0aabf]">
          Invitees can <strong>read</strong> the conversation and ask Hidro for clarifications,
          but only you can post messages, resolve, or change the link. They'll get a notification.
        </div>

        <div className="p-3 shrink-0">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#647087]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search staff…"
              className="hv-input text-sm pl-7 w-full"
              data-testid="eng-share-search"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-6 text-[#647087]"><Loader2 className="w-4 h-4 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-xs text-[#a0aabf] py-6">No matches.</div>
          ) : (
            <ul>
              {filtered.map((u) => {
                const isPicked = picked.has(u.id);
                return (
                  <li key={u.id}>
                    <button
                      onClick={() => toggle(u.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.03] text-sm border-b border-white/5 transition ${isPicked ? "bg-[#1FB6C1]/[0.06]" : ""}`}
                      data-testid={`eng-share-row-${u.id}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isPicked ? "bg-[#1FB6C1] border-[#1FB6C1]" : "border-white/30"}`}>
                        {isPicked && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="font-bold truncate">{u.name || u.email}</div>
                        <div className="text-[10px] text-[#a0aabf]">{u.email}</div>
                      </div>
                      <span className="text-[9px] uppercase tracking-[0.18em] px-1 py-0.5 rounded bg-white/5 border border-white/10 text-[#cbd2df] shrink-0">
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="p-3 border-t border-white/10 flex items-center justify-between gap-2 shrink-0">
          <div className="text-[11px] text-[#a0aabf]">{picked.size} selected</div>
          <button onClick={save} disabled={saving} className="hv-btn hv-btn-primary text-xs" data-testid="eng-share-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
